import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import SharedFooter from '../components/Footer';
import { 
  GraduationCap, 
  ArrowRight,
  CheckCircle,
  Users,
  Clock,
  Video,
  Star,
  BookOpen,
  Calculator,
  ChevronDown
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const Navbar = () => {
  const navigate = useNavigate();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-indigo-700" />
            <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">Gifted Academy Tutors</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/tutors" className="text-slate-600 hover:text-slate-900 transition-colors">Find Tutors</Link>
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</Link>
            <Link to="/about" className="text-slate-600 hover:text-slate-900 transition-colors">About</Link>
            <Link to="/contact" className="text-slate-600 hover:text-slate-900 transition-colors">Contact</Link>
          </div>
          <Button 
            className="rounded-full bg-slate-900 hover:bg-slate-800"
            onClick={() => navigate('/tutors')}
          >
            Get Started
          </Button>
        </div>
      </div>
    </nav>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section className="pt-32 pb-20 md:pb-24 px-6 md:px-12 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
              Toronto's Trusted Math Tutoring Service
            </p>
            <h1 className="font-heading text-5xl md:text-6xl font-medium tracking-tight leading-[1.1] text-slate-900 mb-6">
              Expert Math Tutor in Toronto
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
              Looking for a math tutor in Toronto? We provide private one-on-one math tutoring for high school, IB, and university students. From Grade 9 math to advanced calculus, our qualified tutors help Toronto students build confidence and achieve their academic goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                className="rounded-full bg-slate-900 hover:bg-slate-800 px-8 py-6 text-lg transition-all hover:scale-[1.02] hover:shadow-lg"
                onClick={() => navigate('/tutors')}
              >
                Find a Tutor <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="rounded-full px-8 py-6 text-lg border-slate-200 hover:bg-stone-50"
                onClick={() => navigate('/pricing')}
              >
                View Pricing
              </Button>
            </div>
          </motion.div>

          <motion.div 
            className="relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1596495578065-6e0763fa1178?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Math tutoring session in Toronto"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Calculator className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Math Specialists</p>
                  <p className="text-sm text-slate-500">All levels covered</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const TrustSection = () => {
  const benefits = [
    {
      icon: Users,
      title: "Personalized Support",
      description: "One-on-one sessions tailored to your learning style and pace. No group classes—just focused attention on your math goals."
    },
    {
      icon: Star,
      title: "Qualified Math Tutors",
      description: "Our tutors are experienced educators with strong backgrounds in mathematics, including certified teachers and university graduates."
    },
    {
      icon: Video,
      title: "Flexible Online Tutoring",
      description: "Learn from anywhere in Toronto or the GTA with our convenient online math tutoring sessions via Zoom."
    },
    {
      icon: CheckCircle,
      title: "Confidence Building",
      description: "We help students overcome math anxiety and build the confidence they need to tackle challenging problems."
    },
    {
      icon: BookOpen,
      title: "Exam Preparation",
      description: "Targeted support for tests, finals, IB assessments, and university entrance preparation."
    },
    {
      icon: Clock,
      title: "Flexible Scheduling",
      description: "Book sessions that fit your schedule—evenings, weekends, and last-minute help available."
    }
  ];

  return (
    <section className="py-20 md:py-24 px-6 md:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div className="text-center mb-16" {...fadeUp}>
          <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
            Why Families Choose Us
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-900 mb-4">
            Private Math Tutoring Toronto Families Trust
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Gifted Academy Tutors has helped hundreds of Toronto students improve their math grades and build lasting confidence.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              {...fadeUp}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="border-stone-100 h-full hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-4">
                    <benefit.icon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SubjectsSection = () => {
  const subjects = [
    { name: "Grade 9 Math", description: "Build a strong foundation in algebra, geometry, and problem-solving" },
    { name: "Grade 10 Math", description: "Master quadratics, trigonometry, and analytical geometry" },
    { name: "Functions (Grade 11)", description: "Understand exponential, trigonometric, and polynomial functions" },
    { name: "Advanced Functions", description: "Prepare for calculus with advanced function concepts" },
    { name: "Calculus & Vectors", description: "Expert calculus tutoring for derivatives, integrals, and vectors" },
    { name: "IB Math", description: "Specialized support for IB Math SL and HL students" },
    { name: "University Prep", description: "Get ready for university-level mathematics courses" },
    { name: "Data Management", description: "Statistics, probability, and data analysis support" }
  ];

  return (
    <section className="py-20 md:py-24 px-6 md:px-12 bg-stone-50">
      <div className="max-w-7xl mx-auto">
        <motion.div className="text-center mb-16" {...fadeUp}>
          <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
            Comprehensive Math Support
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-900 mb-4">
            Math Subjects We Tutor in Toronto
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From high school math to IB and university preparation, our Toronto math tutors cover all levels and curricula.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {subjects.map((subject, index) => (
            <motion.div
              key={index}
              {...fadeUp}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl p-6 border border-stone-200 hover:border-indigo-200 hover:shadow-md transition-all"
            >
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                {subject.name}
              </h3>
              <p className="text-sm text-slate-600">
                {subject.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Choose a Tutor",
      description: "Browse our qualified math tutors in Toronto and find the perfect match for your learning needs."
    },
    {
      number: "02",
      title: "Select a Package",
      description: "Choose a tutoring package that fits your budget and schedule—no long-term commitments required."
    },
    {
      number: "03",
      title: "Schedule Sessions",
      description: "Book your sessions at times that work for you—evenings, weekends, or whenever you need help."
    },
    {
      number: "04",
      title: "Start Learning",
      description: "Connect with your tutor online and start improving your math skills with personalized support."
    }
  ];

  return (
    <section className="py-20 md:py-24 px-6 md:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div className="text-center mb-16" {...fadeUp}>
          <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
            Simple Process
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-900 mb-4">
            How to Get Started with Math Tutoring
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Finding a math tutor in Toronto has never been easier. Get started in four simple steps.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              {...fadeUp}
              transition={{ delay: index * 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-full bg-indigo-600 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                {step.number}
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                {step.title}
              </h3>
              <p className="text-slate-600">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = React.useState(null);

  const faqs = [
    {
      question: "How do I find a math tutor in Toronto?",
      answer: "Finding a math tutor in Toronto is easy with Gifted Academy Tutors. Simply browse our tutors page, view tutor profiles and qualifications, then select a package and book your first session. We match you with experienced math tutors who specialize in your specific subject area."
    },
    {
      question: "Do you offer private math tutoring in Toronto?",
      answer: "Yes, we specialize in private one-on-one math tutoring for Toronto students. All our sessions are personalized to your learning style and academic goals. Unlike group classes, our private tutoring ensures focused attention and customized lesson plans."
    },
    {
      question: "Do you help with IB Math?",
      answer: "Absolutely. We have experienced IB math tutors in Toronto who understand the International Baccalaureate curriculum. Whether you're taking IB Math SL or HL, our tutors can help with internal assessments, exam preparation, and mastering challenging concepts."
    },
    {
      question: "Do you offer calculus tutoring in Toronto?",
      answer: "Yes, calculus is one of our most requested subjects. Our calculus tutors in Toronto help students with derivatives, integrals, limits, and applications. We support both high school calculus (MCV4U) and university-level calculus courses."
    },
    {
      question: "Are tutoring sessions online?",
      answer: "Yes, all our math tutoring sessions are conducted online via Zoom. This makes it convenient for students anywhere in Toronto or the GTA to access quality tutoring from home. Online sessions include screen sharing, digital whiteboards, and recorded lessons for review."
    },
    {
      question: "What math levels do you tutor?",
      answer: "We tutor all math levels from Grade 9 through university. This includes Grade 9-10 math, Functions, Advanced Functions, Calculus and Vectors, Data Management, IB Math, and university preparation courses."
    }
  ];

  return (
    <section className="py-20 md:py-24 px-6 md:px-12 bg-stone-50">
      <div className="max-w-3xl mx-auto">
        <motion.div className="text-center mb-12" {...fadeUp}>
          <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
            Common Questions
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600">
            Everything you need to know about our math tutoring services in Toronto.
          </p>
        </motion.div>

        <motion.div className="space-y-4" {...fadeUp} transition={{ delay: 0.1 }}>
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-stone-200 overflow-hidden"
            >
              <button
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-stone-50 transition-colors"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="font-semibold text-slate-900">{faq.question}</span>
                <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-4">
                  <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 md:py-24 px-6 md:px-12 bg-slate-900">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div {...fadeUp}>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-white mb-6">
            Ready to Improve Your Math Grades?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join hundreds of Toronto students who have transformed their math performance with Gifted Academy Tutors. Book your first session today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="rounded-full bg-white text-slate-900 hover:bg-stone-100 px-8 py-6 text-lg"
              onClick={() => navigate('/tutors')}
            >
              Find a Math Tutor <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="rounded-full border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg"
              onClick={() => navigate('/pricing')}
            >
              View Pricing
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const MathTutorTorontoPage = () => {
  return (
    <div className="min-h-screen bg-stone-50">
      <SEO 
        title="Math Tutor Toronto | Private Math Tutoring"
        description="Looking for a math tutor in Toronto? Gifted Academy Tutors offers private one-on-one math tutoring for high school, IB, calculus, and advanced functions students in Toronto."
      />
      <Navbar />
      <HeroSection />
      <TrustSection />
      <SubjectsSection />
      <HowItWorksSection />
      <FAQSection />
      <CTASection />
      <SharedFooter />
    </div>
  );
};

export default MathTutorTorontoPage;
